// filepath: apps/web/src/features/cabinet/AdminSection.tsx
//
// FR-UI-CABINET-1 AC-7 — the Admin section content view.
//
// Rendered at `/admin` (the Admin nav item's target) inside the
// shell. Displays a grid of navigation tiles, each deep-linking an
// EXISTING admin route (see `ADMIN_TILES` in `types.ts`). No
// individual admin page is created or modified by this FR — this is
// pure navigation composition.
//
// VISIBILITY (AC-2 / AC-7 / INV-2): the grid is only reachable when
// `useNavManifest().adminAccess` is `true`, because (a) the sidebar
// only renders the Admin link when `adminAccess`, and (b) this
// component additionally fails-closed by rendering nothing when
// `adminAccess` is `false` (defense in depth — a user who types
// `/admin` directly without the admin role sees an empty state, not
// the tile grid). The SPA never authorizes; every tile target is
// server-RBAC-gated regardless.
//
// INV-9: every tile label renders as a React text node via
// `t(labelKey, fallback)`. Zero `dangerouslySetInnerHTML`.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { useNavManifest } from "./useNavManifest";
import { navigate } from "./usePathname";
import { ADMIN_TILES } from "./types";
import { NEUTRAL, SPACE, RADIUS_MD } from "./styles";

/**
 * AdminSection — grid of admin navigation tiles.
 *
 * Each tile is a real `<a href>` (keyboard-focusable, Enter-to-
 * activate — FR-UI-CORE-9) whose `onClick` calls `navigate(route)`
 * and prevents default, mirroring `DrilldownLink`. The tile grid
 * composes the existing admin areas under one landing.
 */
export function AdminSection(): React.ReactElement {
  const { t } = useTranslation();
  const manifest = useNavManifest();

  // Fail-closed (defense in depth). The sidebar hides the Admin link
  // when adminAccess is false; this guard additionally ensures a
  // direct `/admin` URL by a non-admin renders an empty state rather
  // than the tile grid. The server is the real boundary on every
  // tile target.
  if (!manifest.adminAccess) {
    return (
      <div data-testid="cabinet-admin-denied" style={{ padding: SPACE[6] }}>
        <p style={{ color: NEUTRAL.textSecondary, fontSize: "14px" }}>
          {t(
            "cabinet.admin.denied",
            "You do not have access to the admin section.",
          )}
        </p>
      </div>
    );
  }

  return (
    <div data-testid="cabinet-admin-section" style={{ padding: SPACE[6] }}>
      <header style={{ marginBottom: SPACE[6] }}>
        <h2
          style={{
            fontSize: "20px",
            fontWeight: 700,
            color: NEUTRAL.textPrimary,
            margin: 0,
          }}
        >
          {t("cabinet.admin.title", "Admin")}
        </h2>
        <p
          style={{
            fontSize: "14px",
            color: NEUTRAL.textSecondary,
            margin: "4px 0 0 0",
          }}
        >
          {t("cabinet.admin.subtitle", "Manage your tenant configuration.")}
        </p>
      </header>

      <ul
        aria-label={t("cabinet.admin.grid_label", "Admin areas")}
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
          gap: SPACE[3],
          listStyle: "none",
          margin: 0,
          padding: 0,
        }}
      >
        {ADMIN_TILES.map((tile) => (
          <li key={tile.id} style={{ listStyle: "none" }}>
            <AdminTileLink tile={tile} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function AdminTileLink({
  tile,
}: {
  tile: (typeof ADMIN_TILES)[number];
}): React.ReactElement {
  const { t } = useTranslation();

  const onClick = (e: React.MouseEvent<HTMLAnchorElement>): void => {
    if (
      e.defaultPrevented ||
      e.button !== 0 ||
      e.metaKey ||
      e.altKey ||
      e.ctrlKey ||
      e.shiftKey
    ) {
      return;
    }
    e.preventDefault();
    navigate(tile.route);
  };

  return (
    <a
      href={tile.route}
      data-testid={`cabinet-admin-tile-${tile.id}`}
      onClick={onClick}
      style={{
        display: "block",
        padding: SPACE[4],
        background: NEUTRAL.surfaceCard,
        border: `1px solid ${NEUTRAL.borderDefault}`,
        borderRadius: RADIUS_MD,
        textDecoration: "none",
        color: NEUTRAL.textPrimary,
        fontSize: "14px",
        fontWeight: 500,
        cursor: "pointer",
      }}
    >
      {t(tile.labelKey, tile.fallback)}
    </a>
  );
}
